import type {
  EmployeeEdge,
  GetProfilesQueryResult,
  ProfileFragment,
  ResidentEdge,
} from "@/graphql";
import { Profile_Type } from "@/graphql/generated-types";
import type { IMultiSelectItem, IOption } from "@/interfaces";

const toMultiSelectItem = (item: ProfileFragment): IMultiSelectItem => {
  return {
    id: item.id,
    title: toFullName(item),
    imageURL: item.image?.source,
    blurhash: item.image?.blurhash,
  };
};

const toFullName = (item: ProfileFragment): string => {
  return [item.name || "", item.surname || ""].join(" ").trim();
};

const toOption = (item: ProfileFragment): IOption => {
  return {
    value: item.id,
    title: toFullName(item),
  };
};

const fromEmployeeToProfile = (employeeEdges?: EmployeeEdge[] | null) => {
  if (!employeeEdges) {
    return [];
  }

  const results: ProfileFragment[] = [];
  employeeEdges.forEach((item) => {
    if (item.node.__typename === "Employee") {
      results.push({
        id: item.node.id,
        name: item.node.name || "",
        surname: item.node.surname || "",
        type: item.node.type,
        departments: item.node.departments,
        image: item.node.image,
        settings: item.node.settings,
      });
    }
  });

  return results;
};

const fromResidentToProfile = (residents?: ResidentEdge[] | null) => {
  if (!residents) {
    return [];
  }

  const results: ProfileFragment[] = [];
  residents.forEach((item) => {
    if (item.node.__typename === "Resident") {
      results.push({
        id: item.node.id,
        name: item.node.name || "",
        surname: item.node.surname || "",
        type: Profile_Type.Resident,
        departments: item.node.departments,
        image: item.node.image,
        settings: item.node.settings,
      });
    }
  });

  return results;
};

const toProfileFragment = (
  result: GetProfilesQueryResult,
): ProfileFragment[] => {
  const { employees, residents } = result;

  return [
    ...fromEmployeeToProfile(employees?.edges),
    ...fromResidentToProfile(residents?.edges),
  ];
};

export default {
  toMultiSelectItem,
  toOption,
  toProfileFragment,
};
